import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconoAprobar } from './Iconos';

/** Lo que el visor sabe del tiempo de la página a la vista. */
export type Cronometro =
  | { pagina: number; totalMs: number; restanteMs: number; estado: 'corriendo'; desde: number }
  | { pagina: number; totalMs: number; restanteMs: number; estado: 'pausa' | 'registrando' };

/**
 * Cómo va la página a la vista: cuánto le falta, si está en pausa y que ya
 * quedó leída (ADR-0028).
 *
 * Que se vea es parte del control. Sin esto la persona no sabe por qué una
 * página que «ya vio» no cuenta, y lo resuelve pasando de largo; con esto ve
 * que cada página pide su tiempo y que el portal la anotó.
 */
export function EstadoDeLaPagina({
  pagina,
  leida,
  recien,
  cronometro,
}: {
  pagina: number;
  leida: boolean;
  /** Recién la dio por leída el servidor: se anuncia con un golpe de vista. */
  recien: boolean;
  cronometro: Cronometro | null;
}) {
  const { t } = useTranslation();
  const [, setVuelta] = useState(0);

  // El número de segundos se mueve cada segundo; la barra se llena sola, con CSS.
  useEffect(() => {
    if (cronometro?.estado !== 'corriendo') return;
    const reloj = window.setInterval(() => setVuelta((n) => n + 1), 1000);
    return () => window.clearInterval(reloj);
  }, [cronometro]);

  if (leida) {
    return (
      <span
        className={`pagina-estado pagina-estado--leida${recien ? ' pagina-estado--recien' : ''}`}
        role="status"
      >
        <IconoAprobar />
        {t('viewer.pageTimer.read', { page: pagina })}
      </span>
    );
  }
  if (!cronometro || cronometro.pagina !== pagina) return null;

  const restante =
    cronometro.estado === 'corriendo'
      ? Math.max(0, cronometro.restanteMs - (Date.now() - cronometro.desde))
      : cronometro.restanteMs;
  const segundos = Math.ceil(restante / 1000);
  // Lo que ya llevaba al empezar este tramo: la barra sigue desde ahí, no desde cero.
  const llevaba = 1 - cronometro.restanteMs / cronometro.totalMs;

  return (
    <span className={`pagina-estado pagina-estado--${cronometro.estado}`} title={t('viewer.pageTimer.help')}>
      <span className="pagina-estado__barra" aria-hidden="true">
        <span
          key={cronometro.estado === 'corriendo' ? cronometro.desde : cronometro.estado}
          style={
            cronometro.estado === 'corriendo'
              ? { ['--desde' as string]: llevaba, animationDuration: `${cronometro.restanteMs}ms` }
              : { transform: `scaleX(${cronometro.estado === 'registrando' ? 1 : llevaba})` }
          }
        />
      </span>
      {cronometro.estado === 'corriendo' && t('viewer.pageTimer.running', { page: pagina, seconds: segundos })}
      {cronometro.estado === 'pausa' && t('viewer.pageTimer.paused', { page: pagina, seconds: segundos })}
      {cronometro.estado === 'registrando' && t('viewer.pageTimer.saving', { page: pagina })}
    </span>
  );
}
